import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Loader2 } from 'lucide-react'
import {
  useEvidenceEndpointIds,
  useEvidenceNumbers,
  useEvidenceSummary,
  useRegisterEvidence,
} from '../../../hooks/use-evidence-numbers'
import { useEndpoints } from '../../../hooks/use-endpoints'
import type {
  EvidenceNumber,
  EvidenceNumberFilter,
  EvidenceStatus,
  EvidenceNumberType,
} from '../../../types/evidence-numbers'
import { EvidenceSummaryCards } from './EvidenceSummaryCards'
import { RegisterScopeToggle } from './RegisterScopeToggle'
import { EvidenceTable } from './EvidenceTable'
import { useRegisterScope } from './use-register-scope'

interface EvidenceRegisterViewProps {
  caseId: string
  onSelectEvidence: (evidenceNumber: EvidenceNumber) => void
}

interface FilterBarProps {
  filter: EvidenceNumberFilter
  onFilterChange: (filter: EvidenceNumberFilter) => void
  onRegisterClick: () => void
  hasUnregistered: boolean
}

function FilterBar({
  filter,
  onFilterChange,
  onRegisterClick,
  hasUnregistered,
}: FilterBarProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-48 flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('filter.searchPlaceholder')}
          value={filter.search ?? ''}
          onChange={(ev) => {
            const val = ev.target.value
            onFilterChange({ ...filter, search: val.length > 0 ? val : undefined, page: 1 })
          }}
          className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
      <select
        value={filter.status ?? ''}
        onChange={(ev) => {
          const val = ev.target.value as EvidenceStatus | ''
          onFilterChange({ ...filter, status: val || undefined, page: 1 })
        }}
        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('filter.allStatuses')}</option>
        <option value="registered">{t('status.registered')}</option>
        <option value="collection_requested">{t('status.collection_requested')}</option>
        <option value="collection_sent">{t('status.collection_sent')}</option>
        <option value="data_returned">{t('status.data_returned')}</option>
        <option value="data_ingested">{t('status.data_ingested')}</option>
        <option value="data_exported">{t('status.data_exported')}</option>
      </select>
      <select
        value={filter.numberType ?? ''}
        onChange={(ev) => {
          const val = ev.target.value as EvidenceNumberType | ''
          onFilterChange({ ...filter, numberType: val || undefined, page: 1 })
        }}
        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('filter.allTypes')}</option>
        <option value="base">{t('type.base')}</option>
        <option value="collection_amendment">{t('type.collection_amendment')}</option>
      </select>
      {hasUnregistered && (
        <button
          onClick={onRegisterClick}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Register Evidence Number
        </button>
      )}
    </div>
  )
}

// =============================================================================
// Register Evidence Dialog
// =============================================================================

interface RegisterDialogProps {
  caseId: string
  unregisteredEndpoints: Array<{ id: string; hostname: string }>
  onClose: () => void
  onRegistered: () => void
}

function RegisterDialogHeader({
  unregisteredEndpoints,
  error,
}: {
  unregisteredEndpoints: Array<{ id: string; hostname: string }>
  error: string | null
}): React.ReactElement {
  return (
    <>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Register Evidence Number
      </h3>
      <p className="mt-1 text-sm text-[var(--text-muted)]">
        {unregisteredEndpoints.length} infrastructure item
        {unregisteredEndpoints.length !== 1 ? 's' : ''} without evidence numbers
      </p>
      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}
    </>
  )
}

function EndpointSelect({
  unregisteredEndpoints,
  selectedEndpointId,
  onChange,
}: {
  unregisteredEndpoints: Array<{ id: string; hostname: string }>
  selectedEndpointId: string
  onChange: (id: string) => void
}): React.ReactElement {
  return (
    <div className="mt-4 space-y-2">
      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
        Select infrastructure item
      </label>
      <select
        value={selectedEndpointId}
        onChange={(ev) => {
          onChange(ev.target.value)
        }}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">-- Select --</option>
        {unregisteredEndpoints.map((ep) => (
          <option key={ep.id} value={ep.id}>
            {ep.hostname}
          </option>
        ))}
      </select>
    </div>
  )
}

interface RegisterDialogActionsProps {
  registering: boolean
  unregisteredCount: number
  selectedEndpointId: string
  onClose: () => void
  onRegister: () => Promise<void>
  onRegisterAll: () => Promise<void>
}

function RegisterDialogActions({
  registering,
  unregisteredCount,
  selectedEndpointId,
  onClose,
  onRegister,
  onRegisterAll,
}: RegisterDialogActionsProps): React.ReactElement {
  return (
    <div className="mt-6 flex items-center justify-between">
      <button
        onClick={() => {
          void onRegisterAll()
        }}
        disabled={registering || unregisteredCount === 0}
        className="text-sm text-[var(--accent)] hover:underline disabled:opacity-50"
      >
        {registering ? 'Registering...' : `Register all (${unregisteredCount.toString()})`}
      </button>
      <div className="flex gap-2">
        <button
          onClick={onClose}
          disabled={registering}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            void onRegister()
          }}
          disabled={registering || !selectedEndpointId}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {registering && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Register
        </button>
      </div>
    </div>
  )
}

function RegisterEvidenceDialog({
  caseId,
  unregisteredEndpoints,
  onClose,
  onRegistered,
}: RegisterDialogProps): React.ReactElement {
  const { registering, error, register, clearError } = useRegisterEvidence(caseId)
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('')

  const handleRegister = useCallback(async () => {
    if (!selectedEndpointId) return
    clearError()
    const result = await register({ endpointId: selectedEndpointId })
    if (result) {
      onRegistered()
      onClose()
    }
  }, [selectedEndpointId, register, clearError, onRegistered, onClose])

  const handleRegisterAll = useCallback(async () => {
    clearError()
    for (const ep of unregisteredEndpoints) {
      const result = await register({ endpointId: ep.id })
      if (!result) return
    }
    onRegistered()
    onClose()
  }, [unregisteredEndpoints, register, clearError, onRegistered, onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-6 shadow-xl">
        <RegisterDialogHeader unregisteredEndpoints={unregisteredEndpoints} error={error} />
        <EndpointSelect
          unregisteredEndpoints={unregisteredEndpoints}
          selectedEndpointId={selectedEndpointId}
          onChange={setSelectedEndpointId}
        />
        <RegisterDialogActions
          registering={registering}
          unregisteredCount={unregisteredEndpoints.length}
          selectedEndpointId={selectedEndpointId}
          onClose={onClose}
          onRegister={handleRegister}
          onRegisterAll={handleRegisterAll}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Table Row
// =============================================================================

interface FetchErrorViewProps {
  onRetry: () => void
}

function FetchErrorView({ onRetry }: FetchErrorViewProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="max-w-md text-center">
        <p className="text-red-600 dark:text-red-400">{t('error.fetchFailed')}</p>
        {/* FR10: say what it means and what to do, not just that it failed. */}
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          {t('error.fetchFailedDescription')}
        </p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-[var(--accent)] hover:underline"
        >
          {t('common:actions.retry', 'Retry')}
        </button>
      </div>
    </div>
  )
}

export function EvidenceRegisterView({
  caseId,
  onSelectEvidence,
}: EvidenceRegisterViewProps): React.ReactElement {
  // FR8: the register opens on the custody flow. Most rows on a real case are
  // bare registrations, which buried the items actually in motion.
  const [filter, setFilter] = useState<EvidenceNumberFilter>({ activeOnly: true })
  const [showRegisterDialog, setShowRegisterDialog] = useState(false)

  const { summary, loading: summaryLoading, refetch: refetchSummary } = useEvidenceSummary(caseId)
  const { data, total, page, perPage, loading, error, refetch } = useEvidenceNumbers(caseId, filter)
  const { endpoints } = useEndpoints(caseId)

  // Was a second useEvidenceNumbers instance with `perPage: 1000`. The backend clamps perPage to
  // 100, so on a case with more than 100 evidence numbers this set was silently short and already
  // registered endpoints were offered for registration again. The dedicated projection returns
  // every id and reports if it ever had to truncate.
  const { endpointIds: registeredEndpointIds } = useEvidenceEndpointIds(caseId)

  const unregisteredEndpoints = useMemo(() => {
    const registeredIds = new Set(registeredEndpointIds)
    return endpoints
      .filter((ep) => !registeredIds.has(ep.id))
      .map((ep) => ({ id: ep.id, hostname: ep.hostname || ep.name }))
  }, [endpoints, registeredEndpointIds])

  const handleFilterChange = useCallback((newFilter: EvidenceNumberFilter) => {
    setFilter(newFilter)
  }, [])

  const handleRegistered = useCallback(() => {
    void refetch()
    void refetchSummary()
  }, [refetch, refetchSummary])

  const handlePageChange = useCallback((pg: number) => {
    setFilter((prev) => ({ ...prev, page: pg }))
  }, [])

  const handleScopeChange = useCallback((activeOnly: boolean) => {
    // Reset to page 1: the current page may not exist in the narrower scope.
    setFilter((prev) => ({ ...prev, activeOnly, page: 1 }))
  }, [])

  const scope = useRegisterScope(filter, summary)

  if (error) {
    return <FetchErrorView onRetry={() => { void refetch() }} />
  }

  return (
    <div className="space-y-4">
      <EvidenceSummaryCards summary={summary} loading={summaryLoading} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RegisterScopeToggle
          activeOnly={scope.activeOnly}
          inFlowCount={scope.inFlowCount}
          totalCount={scope.totalAll}
          onChange={handleScopeChange}
        />
      </div>
      <FilterBar
        filter={filter}
        onFilterChange={handleFilterChange}
        onRegisterClick={() => { setShowRegisterDialog(true) }}
        hasUnregistered={unregisteredEndpoints.length > 0}
      />
      <EvidenceTable
        data={data}
        total={total}
        page={page}
        perPage={perPage}
        activeOnly={scope.activeOnly}
        hasFilters={scope.hasFilters}
        registeredTotal={scope.totalAll}
        loading={loading}
        onSelect={onSelectEvidence}
        onPageChange={handlePageChange}
      />
      {showRegisterDialog && (
        <RegisterEvidenceDialog
          caseId={caseId}
          unregisteredEndpoints={unregisteredEndpoints}
          onClose={() => { setShowRegisterDialog(false) }}
          onRegistered={handleRegistered}
        />
      )}
    </div>
  )
}

